import {Order} from "../../Types/Order";

import {OrderFrontStatus} from "../../Enums/Order";

import {tmsToFormat} from "../Dates/DateFormat";
import {MessageType} from "../../Enums/Messages";

/**
 * Returns the payment status label for the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The label for the payment status.
 */
export const getOrderPaymentStatusLabel = (order: Order): string => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
        case OrderFrontStatus.canceled_can_not_resume:
        case OrderFrontStatus.refunded:
            return "Annulée";
        case OrderFrontStatus.paiement_failed_can_not_resume:
        case OrderFrontStatus.paiement_failed_can_resume:
        case OrderFrontStatus.paiement_3ds:
            return "Erreur paiement";
        case OrderFrontStatus.delivered:
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
            return "Payée";
        default:
            return "En attente";
    }
}

/**
 * Returns the payment status type for the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The type of the payment status.
 */
export const getOrderPaymentStatusType = (order: Order): string => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
        case OrderFrontStatus.canceled_can_not_resume:
        case OrderFrontStatus.refunded:
            return MessageType.Error;
        case OrderFrontStatus.paiement_failed_can_not_resume:
        case OrderFrontStatus.paiement_failed_can_resume:
        case OrderFrontStatus.paiement_3ds:
            return MessageType.Warning;
        case OrderFrontStatus.delivered:
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
        default:
            return MessageType.Success;
    }
};

/**
 * Returns the payment status of the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {{label: string, type: string}} - An object containing the label and type of the payment status.
 */
export const getOrderPaymentStatus = (order: Order): { label: string, type: string } => {
    return {
        label: getOrderPaymentStatusLabel(order), type: getOrderPaymentStatusType(order)
    }
}

/**
 * Returns the delivery status label for the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The label for the delivery status.
 */
export const getOrderDeliveryStatusLabel = (order: Order): string => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
        case OrderFrontStatus.canceled_can_not_resume:
        case OrderFrontStatus.refunded:
        case OrderFrontStatus.paiement_failed_can_not_resume:
            return "Annulée";
        case OrderFrontStatus.delivered:
            return "Livrée";
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
        case OrderFrontStatus.validated:
        case OrderFrontStatus.paiement_3ds:
        case OrderFrontStatus.paiement_failed_can_resume:
        default:
            return "À venir";
    }
}

/**
 * Returns the delivery status type for the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The type of the delivery status.
 */
export const getOrderDeliveryStatusType = (order: Order): string => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
        case OrderFrontStatus.canceled_can_not_resume:
        case OrderFrontStatus.refunded:
        case OrderFrontStatus.paiement_failed_can_not_resume:
            return MessageType.Error;
        case OrderFrontStatus.delivered:
            return MessageType.Success;
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
        case OrderFrontStatus.validated:
        case OrderFrontStatus.paiement_3ds:
        case OrderFrontStatus.paiement_failed_can_resume:
        default:
            return MessageType.Neutral;
    }
}

/**
 * Returns the delivery status for the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {{label: string, type: string}} - An object containing the label and type of the delivery status.
 */
export const getOrderDeliveryStatus = (order: Order): { label: string, type: string } => {
    return {
        label: getOrderDeliveryStatusLabel(order), type: getOrderDeliveryStatusType(order)
    }
}

/**
 * Returns a label for the given order and timeslot.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The label for the order status.
 */
export const getOrderStatusLabel = (order: Order): string => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
        case OrderFrontStatus.canceled_can_not_resume:
        case OrderFrontStatus.refunded:
            return "Annulée";
        case OrderFrontStatus.paiement_failed_can_not_resume:
        case OrderFrontStatus.paiement_failed_can_resume:
        case OrderFrontStatus.paiement_3ds:
            return "Erreur paiement";
        case OrderFrontStatus.delivered:
            return "Livrée";
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
            return "Payée";
        case OrderFrontStatus.validated:
            return "En cours";
        default:
            return "En attente";
    }
};

/**
 * Returns a status message for the given order and timeslot.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The status message for the order.
 */
export const getOrderStatusMessage = (order: Order): string | undefined => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
            return '<b>Annulée&nbsp;:&nbsp;</b>votre commande à été annulée. Vous pouvez la réactiver au plus tard 2 jours avant la livraison.'
        case OrderFrontStatus.paiement_failed_can_not_resume:
        case OrderFrontStatus.canceled_can_not_resume:
            return '<b>Annulée&nbsp;:&nbsp;</b>votre commande à été annulée et il est trop tard pour la réactiver. Vous n’avez pas été prélevé.'
        case OrderFrontStatus.refunded:
            return "<b>Annulée&nbsp;:&nbsp;</b>votre commande à été annulée et remboursée sous forme de coupon à retrouver dans votre compte. ";
        case OrderFrontStatus.validated:
            return `<b>En cours&nbsp;:&nbsp;</b>complétez ou modifiez votre commande jusqu’au ${tmsToFormat(order?.limitDate, "eee d MMM 'à' HH'h'mm")}.`
        case OrderFrontStatus.paiement_3ds:
        case OrderFrontStatus.paiement_failed_can_resume:
            return '<b>Erreur paiement&nbsp;:&nbsp;</b>une erreur est survenue lors du paiement de votre commande. Veuillez réessayer.'
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
            return '<b>Payée&nbsp;:&nbsp;</b>votre commande à été payée, elle sera bientôt préparée et livrée par nos équipes.';
        case OrderFrontStatus.delivered:
            return '<b>Livrée&nbsp;:&nbsp;</b>votre commande à été livrée dans votre point relais et n’attend plus que vous !';
        default:
            return undefined;
    }
};

/**
 * Returns the status type for the given order.
 * @param {Order} order - The order object containing the front status.
 * @returns {string} - The type of the order status.
 */
export const getOrderStatusType = (order: Order): string => {
    switch (order?.frontStatus) {
        case OrderFrontStatus.canceled_can_resume:
        case OrderFrontStatus.canceled_can_not_resume:
        case OrderFrontStatus.refunded:
        case OrderFrontStatus.paiement_failed_can_not_resume:
            return MessageType.Error;
        case OrderFrontStatus.paiement_failed_can_resume:
        case OrderFrontStatus.paiement_3ds:
            return MessageType.Warning;
        case OrderFrontStatus.delivered:
            return MessageType.Success;
        case OrderFrontStatus.payed:
        case OrderFrontStatus.preparation:
        case OrderFrontStatus.validated:
        default:
            return MessageType.Neutral;
    }
}

/**
 * Returns the status for the given order.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {{label: string, message?: string, type: string}} - An object containing the label, message and type of the order status.
 */
export const getOrderStatus = (order: Order): { label: string, message?: string, type: string } => {
    const label = getOrderStatusLabel(order);
    const message = getOrderStatusMessage(order);
    const type = getOrderStatusType(order);
    return {label, message, type};
}

/**
 * Checks if the order status is one of the canceled statuses.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {boolean} - True if the order status is canceled, false otherwise.
 */
export const isOrderCanceledStatus = (order: Order): boolean => [OrderFrontStatus.canceled_can_not_resume, OrderFrontStatus.canceled_can_resume, OrderFrontStatus.paiement_failed_can_not_resume, OrderFrontStatus.refunded]
    .includes(order?.frontStatus);

/**
 * Checks if the order status is one of the paid statuses.
 *
 * @param {Order} order - The order object containing the front status.
 * @returns {boolean} - True if the order status is paid, false otherwise.
 */
export const isOrderPayedStatus = (order: Order): boolean => [OrderFrontStatus.payed, OrderFrontStatus.preparation, OrderFrontStatus.delivered]
    .includes(order?.frontStatus);


/**
 * Checks if the order needs user action.
 *
 * @param {Order} order - The order object containing the front status and payment details.
 * @returns {boolean} - True if the order needs user action, false otherwise.
 */
export const isOrderNeedUserAction = (order: Order): boolean => {
    const isPayableAndNotBatch = !!order?.stripePayment?.isPayable && !order?.canWaitForBatch;
    const isFrontStatusRetry = [OrderFrontStatus.paiement_failed_can_resume, OrderFrontStatus.paiement_3ds].includes(order?.frontStatus);
    return isPayableAndNotBatch || isFrontStatusRetry;
}

export default {
    getOrderPaymentStatus,
    getOrderDeliveryStatus,
    getOrderStatus,
    isOrderCanceledStatus,
    isOrderPayedStatus,
    isOrderNeedUserAction
};
